export const PHONE_LENGTH = 10;

export const REGEX = {
    ONLY_DIGITS: /^\d+$/,
    ONLY_DIGITS_OPTIONAL: /^\d*$/,
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    HAS_UPPERCASE: /[A-Z]/,
    HAS_LOWERCASE: /[a-z]/,
    HAS_NUMBER: /\d/,
    HAS_SPECIAL: /[@$!%*?&#]/,
    NAME_SAFE: /^[a-zA-Z\s.-]+$/,
    ADDRESS_SAFE: /^[a-zA-Z0-9\s,./-]+$/,
    CONTAINS_HTML: /[<>]/,
    GMAIL_DOMAIN: /@gmail\.com$/,
    GMAIL_USERNAME_CHARS: /^[a-z0-9.]+$/,
    ZIPCODE: /^[A-Za-z0-9 -]{3,10}$/
};

/**
 * Sanitizes text by trimming whitespace and removing extra internal spaces.
 * "  John   Doe  " -> "John Doe"
 */
export const sanitizeText = (value) => {
    if (typeof value !== 'string') return '';
    return value.trim().replace(/\s+/g, ' ');
};

/**
 * Sanitizes phone number by removing non-digit characters.
 */
export const sanitizePhone = (value) => {
    if (typeof value !== 'string') return '';
    return value.replace(/\D/g, '');
};

/**
 * Validates phone number.
 */
export const validatePhoneNumber = (phone) => {
    if (!phone) return 'Please enter your phone number.';
    if (!REGEX.ONLY_DIGITS.test(phone)) {
        return 'Phone number should contain only numbers.';
    }
    if (phone.length !== PHONE_LENGTH) {
        return `Phone number must be ${PHONE_LENGTH} digits.`;
    }
    return '';
};

/**
 * Validates full name.
 */
export const validateFullName = (name) => {
    const sanitized = sanitizeText(name);
    if (!sanitized) {
        return 'Please enter your full name.';
    }
    if (sanitized.length < 3) {
        return 'Full name must be at least 3 characters.';
    }
    if (sanitized.length > 50) {
        return 'Full name must not exceed 50 characters.';
    }
    if (REGEX.HAS_NUMBER.test(sanitized)) {
        return 'Full name should not contain numbers.';
    }
    if (REGEX.CONTAINS_HTML.test(name)) {
        return 'HTML tags or script payloads are not allowed.';
    }
    if (!REGEX.NAME_SAFE.test(sanitized)) {
        return 'Only letters, spaces, dots (.), and hyphens (-) are allowed.';
    }
    return '';
};

/**
 * Validates address (optional).
 */
export const validateAddress = (address) => {
    if (!address) return '';
    const sanitized = sanitizeText(address);
    if (REGEX.CONTAINS_HTML.test(address)) {
        return 'HTML tags or script payloads are not allowed.';
    }
    if (!REGEX.ADDRESS_SAFE.test(sanitized)) {
        return 'Address contains invalid characters.';
    }
    return '';
};

/**
 * Validates email addresses (all valid formats).
 */
export const validateGmailEmail = (email) => {
    const trimmed = email.trim();
    if (!trimmed) return 'Please enter your email.';

    if (REGEX.CONTAINS_HTML.test(email)) {
        return 'HTML tags or script payloads are not allowed.';
    }

    // Standard email format validation
    if (!REGEX.EMAIL.test(trimmed)) {
        return 'Please enter a valid email address.';
    }

    return '';
};

/**
 * Validates email addresses (accepts all valid email formats).
 */
export const validateEmail = (email) => {
    return validateGmailEmail(email);
};

/**
 * Validates password based on security rules.
 */
export const validatePassword = (password) => {
    if (!password) return 'Please enter a password.';
    if (REGEX.CONTAINS_HTML.test(password)) {
        return 'HTML tags or script payloads are not allowed.';
    }
    if (password.trim() !== password) {
        return 'Password cannot start or end with spaces.';
    }
    if (password.length < 8) {
        return 'Password must be 8+ characters and include uppercase, lowercase, number, and special character.';
    }
    if (!REGEX.HAS_UPPERCASE.test(password)) {
        return 'Password must include at least one uppercase letter.';
    }
    if (!REGEX.HAS_LOWERCASE.test(password)) {
        return 'Password must include at least one lowercase letter.';
    }
    if (!REGEX.HAS_NUMBER.test(password)) {
        return 'Password must include at least one number.';
    }
    if (!REGEX.HAS_SPECIAL.test(password)) {
        return 'Password must include at least one special character (@$!%*?&).';
    }
    return '';
};

/**
 * Validates password for login (only checks presence and HTML safety).
 */
export const validateLoginPassword = (password) => {
    if (!password) return 'Please enter your password.';
    if (REGEX.CONTAINS_HTML.test(password)) {
        return 'HTML tags or script payloads are not allowed.';
    }
    return '';
};

/**
 * Checks password strength for live feedback.
 */
export const getPasswordStrength = (password) => {
    return {
        hasMinLength: password.length >= 8,
        hasUppercase: REGEX.HAS_UPPERCASE.test(password),
        hasLowercase: REGEX.HAS_LOWERCASE.test(password),
        hasNumber: REGEX.HAS_NUMBER.test(password),
        hasSpecialChar: REGEX.HAS_SPECIAL.test(password)
    };
};

/**
 * Validates zip code.
 */
export const validateZipCode = (zip) => {
    if (!zip) return '';
    const trimmed = zip.trim();
    if (trimmed.length < 3 || trimmed.length > 10) {
        return 'Zip code must be between 3 and 10 characters.';
    }
    if (!REGEX.ZIPCODE.test(trimmed)) {
        return 'Zip code can only contain letters, numbers, spaces, and hyphens.';
    }
    return '';
};
